/* Consent-gated diagnostics only: never duplicate Shopify's ecommerce events. */
if (!customElements.get('hyc-pdp-metrics')) customElements.define('hyc-pdp-metrics', class extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const root = this.closest('hyc-product-story');
    if (!root) return;
    const sent = new Set();
    this.timers = new Map();
    const allowed = () => {
      try {
        return !Shopify.designMode && (!Shopify.theme?.role || Shopify.theme.role === 'main')
          && !location.hostname.endsWith('.shopifypreview.com')
          && !new URLSearchParams(location.search).has('preview_theme_id')
          && Shopify.customerPrivacy?.analyticsProcessingAllowed() === true;
      } catch (_) { return false; }
    };
    const send = (event, parameters = {}) => {
      if (!allowed() || !/^G-[A-Z0-9]+$/.test(this.dataset.measurementId)) return false;
      // Reuse the site's existing Google tag/data layer; do not load another tag.
      if (!window.dataLayer) return false;
      // If the existing tag appeared late, enrolment must precede interaction diagnostics.
      if (event !== 'hyc_pdp_experiment_exposure') exposure();
      const experiment = root.dataset?.experimentState === 'enrolled'
        ? {experiment_id:root.dataset.experimentId, experiment_variant:root.dataset.storyVariant} : {};
      const payload = {send_to:this.dataset.measurementId, pdp_model:'HYC6200X', pdp_version:'layered-v1', ...experiment, ...parameters};
      if (typeof window.gtag === 'function') window.gtag('event', event, payload);
      else (function() { window.dataLayer.push(arguments); })('event', event, payload);
      return true;
    };
    const sectionTargets = [
      ['operator', '.scene'], ['capability', '.section-intro'], ['details', '.ownership-heading'],
      ['workbench', '.workbench-scene'], ['kit', '.kit-copy'], ['video', '.hyc-film-heading'],
      ['support', '.support'], ['specifications', '.spec-heading'], ['closing', '.hyc-purchase']
    ].map(([name, selector]) => ({name, node:root.querySelector(selector)})).filter(item => item.node);
    const names = new Map(sectionTargets.map(item => [item.node, item.name]));
    const clearTimers = () => { this.timers.forEach(clearTimeout); this.timers.clear(); };
    let exposureSent = false;
    const exposure = () => {
      if (!exposureSent && !document.hidden && root.dataset?.experimentState === 'enrolled') {
        exposureSent = send('hyc_pdp_experiment_exposure');
      }
    };
    root.addEventListener('hyc:experiment-ready', exposure, {signal});
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        clearTimeout(this.timers.get(entry.target));
        this.timers.delete(entry.target);
        const name = names.get(entry.target);
        if (!allowed() || document.hidden || !entry.isIntersecting || entry.intersectionRatio < .25 || sent.has(name)) return;
        this.timers.set(entry.target, setTimeout(() => {
          this.timers.delete(entry.target);
          if (!document.hidden && send('hyc_pdp_section_view', {pdp_section:name})) sent.add(name);
        }, 1000));
      });
    }, {threshold:[0, .25]});
    const observe = () => {
      clearTimers(); this.observer.disconnect();
      exposure();
      if (allowed() && !document.hidden) sectionTargets.forEach(item => this.observer.observe(item.node));
    };
    document.addEventListener('visitorConsentCollected', observe, {signal});
    document.addEventListener('visibilitychange', observe, {signal});
    observe();
    // Shopify may initialise its privacy API after this deferred script.
    if (window.Shopify?.loadFeatures) Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}], () => {
      if (!signal.aborted) observe();
    });
    root.addEventListener('click', event => {
      const target = event.target.closest('a,button');
      if (!target) return;
      if (target.matches('[data-film-launch]')) send('hyc_pdp_video_open', {pdp_section:'video'});
      else if (target.matches('[data-step]')) send('hyc_pdp_feature_select', {pdp_feature:['reach','power','handling'][Number(target.dataset.step)]});
      else if (target.matches('[data-detail]')) send('hyc_pdp_detail_select', {pdp_detail:target.dataset.detail});
      else if (target.matches('[data-buy-link]')) send('hyc_pdp_buy_link', {pdp_section:target.closest('.chapter-nav') ? 'navigation' : 'story'});
    }, {signal});
    document.addEventListener('product-ajax:added', event => {
      const form = event.detail?.form;
      if (!(form instanceof HTMLFormElement)) return;
      let position;
      if (form.matches('.hyc-closing-form')) position = 'closing';
      else if (form.matches('.hyc-part-form')) position = 'accessory';
      else if (form.closest('product-info.hyc-buy-main')) position = 'top';
      if (!position) return;
      const product = event.detail.product;
      send('hyc_pdp_cart_added', {pdp_cta_location:position, item_id:String(product.variant_id || product.id), quantity:Number(form.querySelector('[name="quantity"]')?.value || 1)});
    }, {signal});
  }
  disconnectedCallback() {
    this.controller?.abort(); this.controller = null;
    this.observer?.disconnect();
    this.timers?.forEach(clearTimeout); this.timers?.clear();
  }
});
